import React from 'react';
import { Container, Eyebrow, Section } from '../components/ui/Section';
import { PhotoFrame } from '../components/ui/PhotoFrame';
import { ButtonExternal, ButtonLink } from '../components/ui/Button';
import { Reveal, stagger } from '../components/motion/Reveal';
import { FloatingPhoto } from '../components/motion/FloatingPhoto';
import { DoodleStar, DoodleUnderline } from '../components/brand/Doodles';
import { DoodleScatter } from '../components/brand/DoodleScatter';
import { galleryPhotos } from '../data/photos';
import { emailAddress, externalLinks, meeting } from '../data/site';
const pillars = [{
  title: 'Bring In',
  copy: 'Welcome students and help them encounter the Gospel in a genuine Christian community.'
}, {
  title: 'Build Up',
  copy: 'Help students grow in faith, friendship, service, and leadership.'
}, {
  title: 'Send Out',
  copy: 'Equip students to live out their faith and serve on campus and beyond.'
}];
export function About() {
  return <>
      {/* Hero + approved opening copy */}
      <Section className="bcm-paper relative overflow-hidden py-14 pb-24 sm:py-20 sm:pb-28" labelledBy="about-heading" data-id="element-415" data-mp="1ujw6jejebg8z">
        <DoodleScatter tone="paper" seed="about-hero" data-id="element-416" data-mp="23izrko24i5q4" />
        <Container className="relative grid items-start gap-10 lg:grid-cols-[1.05fr_0.8fr] lg:gap-14" data-id="element-417" data-mp="23gdiul3hxk6b">
          <Reveal data-id="element-418" data-mp="1ylhsdpdwr4vb">
            <Eyebrow data-id="element-419" data-mp="2dgnva3hpinkf">About UCF BCM</Eyebrow>
            <h1 id="about-heading" className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-brand-navy sm:text-6xl" data-id="element-420" data-mp="2dfcumcjk5vxj">
              About Us
            </h1>
            <DoodleUnderline className="mt-3 h-3.5 w-36" color="var(--bcm-yellow)" data-id="element-421" data-mp="2mb2a64ybrw30" />

            <p className="mt-6 max-w-xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-422" data-mp="2i85zr1ixv6a5">
              BCM is a place for students to explore the claims of Jesus Christ and experience the
              excitement of answering the call to follow Him.
            </p>
            <p className="mt-4 max-w-xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-423" data-mp="1ovfrehakxlxe">
              BCM stands for Baptist Collegiate Ministries, but you don’t need to be a Baptist, or
              even a Christian, to find a home here. Our students come from all kinds of backgrounds.
              We receive generous support from churches in the Florida Baptist Convention, and we’re
              thankful for it! Those churches–over 2800 of them across the state of Florida–and the
              people who faithfully contribute to them, have committed to invest in this ministry,
              and in you! Everyone is welcome!
            </p>
            <p className="mt-4 max-w-xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-424" data-mp="2gjzn36yphk8o">
              BCM exists to reach out and serve our local college campuses, and BCM @ UCF is here for
              students at the University of Central Florida and surrounding colleges like Valencia
              College, Seminole State and Full Sail University. You can contact us by emailing{' '}
              <a href={externalLinks.email} className="font-semibold text-brand-navy underline underline-offset-4 hover:no-underline" data-id="element-425" data-mp="jpg42lu6qetk">
                {emailAddress}
              </a>{' '}
              and connecting with us on our{' '}
              <a href={externalLinks.instagram} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-navy underline underline-offset-4 hover:no-underline" data-id="element-426" data-mp="501rz5z85jah">
                Social Media
                <span className="sr-only" data-id="element-427" data-mp="smgoyii1pus9"> (opens in a new tab)</span>
              </a>
              !
            </p>
          </Reveal>

          <Reveal x={16} delay={0.12} className="relative" data-id="element-428" data-mp="oosu1fgcs20d">
            <DoodleStar className="absolute -left-4 -top-5 z-10 h-10 w-10" color="var(--bcm-dark-blue)" data-id="element-429" data-mp="3dftxpvs3w507" />
            {/* 4:3 matches the landscape source so the full table of students
                stays in frame instead of being cropped to a portrait sliver. */}
            <FloatingPhoto range={6} duration={7.5} data-id="element-430" data-mp="15waaejc9e0br">
              <PhotoFrame photo={galleryPhotos[1]} ratio="aspect-[4/3]" rotate="-rotate-1" className="transition-transform duration-300 hover:rotate-0" data-id="element-431" data-mp="r6bbufgskmvr" />
            </FloatingPhoto>
            <FloatingPhoto range={4} duration={6} delay={0.9} className="absolute -bottom-8 right-0 w-1/3 sm:w-[36%]" data-id="element-432" data-mp="1jsdx7rksz6e7">
              <PhotoFrame photo={galleryPhotos[2]} ratio="aspect-square" rotate="rotate-3" data-id="element-433" data-mp="1atirshpl20by" />
            </FloatingPhoto>
          </Reveal>
        </Container>
      </Section>

      {/* Mission */}
      <Section className="on-dark relative overflow-hidden bg-brand-navy py-14 text-white sm:py-20" labelledBy="about-mission-heading" data-id="element-434" data-mp="3vigbtm4ijsff">
        <DoodleScatter tone="navy" seed="about-mission" data-id="element-435" data-mp="3s1aofbgaygpw" />
        <Container className="relative" data-id="element-436" data-mp="3s46j8qwuspa4">
          <Reveal data-id="element-437" data-mp="3n3sdjfiq5rf9">
            <p className="font-meta text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue" data-id="element-438" data-mp="3n27gnj8xtwe8">
              Our Mission
            </p>
            <h2 id="about-mission-heading" className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-id="element-439" data-mp="5sj4cmzdk6u7">
              Bring in. Build up. Send out.
            </h2>
          </Reveal>
          <ul className="mt-10 grid gap-8 sm:grid-cols-3" data-id="element-440" data-mp="34dkd556etf2r">
            {pillars.map((pillar, index) => <Reveal key={pillar.title} as="li" delay={stagger(index)} className="border-t-4 border-brand-yellow pt-5" data-id="element-441" data-mp="3j27300tnxp88" data-mpi={index}>
                <h3 className="font-display text-2xl font-bold text-brand-blue" data-id="element-442" data-mp="2qjtdcoihd9k8">{pillar.title}</h3>
                <p className="mt-2 font-sans text-[16px] leading-relaxed text-white" data-id="element-443" data-mp="2ll8f2umsgzyf">
                  {pillar.copy}
                </p>
              </Reveal>)}
          </ul>
        </Container>
      </Section>

      {/* Where BCM meets */}
      <Section className="bcm-paper relative overflow-hidden py-14 sm:py-18" labelledBy="about-location-heading" data-id="element-444" data-mp="3k2quxf5bmk8b">
        <DoodleScatter tone="paper" seed="about-location" data-id="element-445" data-mp="3p0ioedlm6eem" />
        <Container className="relative" data-id="element-446" data-mp="2sbkq4lennycp">
          <Reveal data-id="element-447" data-mp="2scmd9o8y4bas">
            <h2 id="about-location-heading" className="font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-4xl" data-id="element-448" data-mp="2sd45m9shm5df">
              Where BCM meets
            </h2>
            <p className="mt-5 font-meta text-[15px] font-semibold text-brand-navy" data-id="element-449" data-mp="31ec4asyx1881">
              {meeting.summary}
            </p>
            <p className="mt-1 font-meta text-[15px] text-ink" data-id="element-450" data-mp="gmjr98mwomzj">
              Cape Florida Ballroom, Room 316
              <span className="block" data-id="element-451" data-mp="ljsc5gzfv54h">UCF Student Union, 3rd Floor</span>
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-452" data-mp="2635jvausalix">
              <ButtonLink to="/events" data-id="element-453" data-mp="2142pqlgcdzpq">View Events</ButtonLink>
              <ButtonExternal href={externalLinks.studentUnion} variant="secondary" data-id="element-454" data-mp="214krtypor85k">
                UCF Student Union Directions
              </ButtonExternal>
            </div>
          </Reveal>
        </Container>
      </Section>

    </>;
}